/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  BuilderFormSchemaFactory,
  FormRootSchema,
  FormState,
  FormStateSchema,
  whenHasPermission,
} from '@gh/builder-shared';
import { BuilderPermission, ContactInvitationStatus, CreditorType } from '@gh/builder-shared-data';
import { toAppFormGroup } from '@gh/common-shared';
import { getControl } from '@gh/core-ui';
import { ContactDetailsCreditor } from '../../resources/model/contact-details-creditor.model';
import { ContactDetailsCreditorCommonFormState } from '../contact-details-creditor-common-tab/contact-details-creditor-common-form.service';
import { ContactDetailsCreditorSubContractorFormState } from '../contact-details-creditor-sub-contractor-tab/contact-details-creditor-sub-contractor-form.service';
import {
  ContactDetailsSharedCommunicationListFormState,
  ContactDetailsSharedCommunicationListFormStateFactory,
} from '../contact-details-shared-communication-list-tab/contact-details-shared-communication-list-form.service';
import {
  ContactDetailsSharedCustomFieldsFormState,
  ContactDetailsSharedCustomFieldsFormStateFactory,
} from '../contact-details-shared-custom-fields-tab/contact-details-shared-custom-fields-form.service';
import {
  ContactDetailsSharedTermsPayeeFormState,
  ContactDetailsSharedTermsPayeeFormStateFactory,
} from '../contact-details-shared-terms-tab/contact-details-shared-terms-payee-form.service';

@Injectable()
export class ContactDetailsCreditorFormState extends FormState<ContactDetailsCreditor> {
  termsForm: ContactDetailsSharedTermsPayeeFormState;
  customFieldsForm: ContactDetailsSharedCustomFieldsFormState;
  communicationForm: ContactDetailsSharedCommunicationListFormState;

  constructor(schemaFactory: BuilderFormSchemaFactory,
              private commonForm: ContactDetailsCreditorCommonFormState,
              private subContractorForm: ContactDetailsCreditorSubContractorFormState,

              termsFormFactory: ContactDetailsSharedTermsPayeeFormStateFactory,
              communicationFormFactory: ContactDetailsSharedCommunicationListFormStateFactory,
              customFieldsFormFactory: ContactDetailsSharedCustomFieldsFormStateFactory) {
    super(schemaFactory);

    this.termsForm = termsFormFactory.create();
    this.communicationForm = communicationFormFactory.create();
    this.customFieldsForm = customFieldsFormFactory.create();
  }

  build(creditorSupplier: ContactDetailsCreditor): void {
    this.commonForm.build(creditorSupplier.common);
    this.subContractorForm.build(creditorSupplier.subContractor);
    this.termsForm.build(creditorSupplier.terms);
    this.communicationForm.build(creditorSupplier.communication);
    this.customFieldsForm.build(creditorSupplier.customFields);

    this.initSchema(creditorSupplier);
    this.setChildForm('common', this.commonForm);
    this.setChildForm('subContractor', this.subContractorForm);
    this.setChildForm('terms', this.termsForm);
    this.setChildForm('communication', this.communicationForm);
    this.setChildForm('customFields', this.customFieldsForm);
  }

  updateSubContractorForm(): void {
    toAppFormGroup(getControl(this.group, 'subContractor')).updateSchema({
      enabled: this.commonForm.creditorType === CreditorType.SubContractor,
    });
  }

  updateInvitationStatus(status: ContactInvitationStatus): void {
    getControl(this.group, 'invitationStatus').patchValue(status);
  }

  protected buildSchema(creditorSupplier: ContactDetailsCreditor, { root, native }: FormStateSchema): FormRootSchema {
    const hasSubContractor = creditorSupplier.common.type === CreditorType.SubContractor;

    return root({
      enabled: whenHasPermission(BuilderPermission.Contacts.CreditorSupplier.CREATE_EDIT),
      children: {
        common: native(this.commonForm.group),
        subContractor: native({
          enabled: hasSubContractor,
          control: this.subContractorForm.group,
        }),
        terms: native(this.termsForm.group),
        customFields: native(this.customFieldsForm.group),
        communication: native(this.communicationForm.group),
        invitationStatus: { required: false },
      },
    });
  }
}
